import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { OpencareModal } from 'common/components/OpencareModal/OpencareModal';
import { FlexCol } from 'common/styles/oc-styled-components';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useCountdown } from 'utils/customHooks/useCountdown';

import { CLOSE_SOURCES } from '../CheckoutTimer/constants';

import { StyledButton, StyledHeader, StyledP } from './CheckoutTimerEndModal-styled-components';

const CONTINUE_COUNTDOWN_TIME = 20;

const analytics = new Analytics();

interface CheckoutTimerEndModalProps {
  clinicIsInstantBook: boolean;
  handleClose: (source: string) => void;
  handleContinue: () => void;
}

/* Open state should be handled by conditionally rendering this component, otherwise
the countdown will run even while this is closed */
export const CheckoutTimerEndModal = ({
  clinicIsInstantBook,
  handleClose,
  handleContinue,
}: CheckoutTimerEndModalProps) => {
  const analyticsService = useAnalyticsService();

  const [resetTrigger, setResetTrigger] = useState(false);
  const [countdown, setCountdown] = useState(CONTINUE_COUNTDOWN_TIME);

  const handleTick = useCallback((timeLeft: number) => {
    setCountdown(timeLeft);
    if (timeLeft <= 0) {
      handleClose(CLOSE_SOURCES.TIMEOUT);
    }
  }, [handleClose]);

  useCountdown({
    initialTime: CONTINUE_COUNTDOWN_TIME,
    resetTrigger,
    onTick: handleTick,
  });

  // On load, reset existing countdown
  useEffect(() => {
    setResetTrigger((prev) => !prev);
  }, []);

  // analyticsService changes on every page load, so we make sure we only track pageview once
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-checkout-timer-end',
      name: 'checkout-timer-ended',
      ...analyticsService.analyticsData,
    });

    hasTrackedPageView.current = true;
  }, [analyticsService.analyticsData]);

  const ibSpecificText = clinicIsInstantBook
    ? 'keeping the slot available for everyone'
    : 'allowing someone else to secure their booking';

  return (
    <OpencareModal
      centered
      width="350px"
      onClose={() => handleClose(CLOSE_SOURCES.GENERIC)}
      isOpen
      showCloseButton={false}
      maskClosable={false}
    >
      <FlexCol justify="center" align="center" height="100%">
        <StyledHeader>Time's Up!</StyledHeader>
        <StyledP>
          Your checkout timer has expired,
          but we're giving you an extra {CONTINUE_COUNTDOWN_TIME} seconds!
          Click "Continue" now to secure your spot.
        </StyledP>
        <StyledButton
          type="primary"
          shape="round"
          size="large"
          onClick={handleContinue}
        >
          Continue with Request ({countdown})
        </StyledButton>
        <StyledP>
          If you don't click "Continue" within 20 seconds, you'll be sent back
          to the practice profile, {ibSpecificText}.
        </StyledP>
        <StyledP>Thank you for your understanding.</StyledP>
        <StyledButton
          type="secondary"
          onClick={() => handleClose(CLOSE_SOURCES.USER)}
        >
          Return to Practice Profile
        </StyledButton>
      </FlexCol>
    </OpencareModal>
  );
};
